/**
 * Ouverture d'un Blob (PDF CERFA, attestation…) dans un nouvel onglet, avec
 * révocation bornée de l'URL.
 *
 * Un `URL.createObjectURL()` retient le Blob jusqu'à la révocation explicite ou
 * le rechargement complet de l'application. Les boutons d'actions de deal
 * n'appelaient jamais `revokeObjectURL()` : un poste qui enchaîne les dossiers
 * dans la journée accumulait tous les PDF générés en mémoire.
 *
 * On ne révoque pas immédiatement après le `window.open()` — l'onglet doit
 * avoir le temps de charger l'URL, et l'utilisateur peut vouloir y rafraîchir
 * la page. On garde donc les N dernières URL vivantes et on révoque les plus
 * anciennes : la rétention est bornée sans casser les onglets récents.
 */

export interface BlobUrlDeps {
  createObjectURL: (blob: Blob) => string;
  revokeObjectURL: (url: string) => void;
  open: (url: string, target: string) => unknown;
}

export const MAX_TRACKED_BLOB_URLS = 5;

/**
 * Construit un ouvreur qui garde au plus `maxTracked` URL vivantes.
 * Les dépendances sont injectables pour les tests (environnement `node`).
 */
export const createBlobUrlOpener = (
  deps: BlobUrlDeps,
  maxTracked: number = MAX_TRACKED_BLOB_URLS,
): ((blob: Blob) => string) => {
  const trackedUrls: string[] = [];

  return (blob: Blob): string => {
    const url = deps.createObjectURL(blob);
    deps.open(url, '_blank');

    trackedUrls.push(url);
    const expiredUrls = trackedUrls.splice(0, Math.max(0, trackedUrls.length - maxTracked));
    expiredUrls.forEach((expiredUrl) => deps.revokeObjectURL(expiredUrl));

    return url;
  };
};

// Les accès à `URL` / `window` sont dans le corps des lambdas : le module reste
// importable hors navigateur (tests en environnement `node`).
const browserOpener = createBlobUrlOpener({
  createObjectURL: (blob) => URL.createObjectURL(blob),
  revokeObjectURL: (url) => URL.revokeObjectURL(url),
  open: (url, target) => window.open(url, target),
});

export const openBlobInNewTab = (blob: Blob): string => browserOpener(blob);
